/**
 * Tickets unfold inside their row, never as a modal over live prices, and nothing
 * commits from a list without one.
 */
import clsx from 'clsx';
import { type ReactNode, useEffect, useRef } from 'react';
import { fmtRupees } from '../../lib/format.ts';
import { Button } from './button.tsx';
import { Checkbox } from './controls.tsx';
import { Eyebrow } from './shell.tsx';

export function Ticket({
  title,
  onClose,
  children,
  cancel = 'Cancel',
  confirm,
}: {
  title?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  cancel?: string;
  confirm: {
    label: ReactNode;
    tone?: 'primary' | 'danger';
    busy?: boolean;
    disabled?: boolean;
    onClick: () => void;
    /** The label is a figure: keep it out of session recordings. */
    redacted?: boolean;
  };
}) {
  const box = useRef<HTMLDivElement>(null);
  const shut = useRef(onClose);
  shut.current = onClose;

  useEffect(() => {
    box.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, []);

  // Scoped to focus, not global: popovers and menus also listen for Escape.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!box.current?.contains(document.activeElement)) return;
      shut.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={box}
      className="ticket-in mt-1 flex max-w-3xl flex-col gap-2.5 rounded-box border border-line bg-surface-raised px-3 py-2.5"
    >
      {title && <Eyebrow as="p">{title}</Eyebrow>}
      {children}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button onClick={onClose}>{cancel}</Button>
        <Button
          tone={confirm.tone ?? 'primary'}
          busy={confirm.busy}
          disabled={confirm.disabled}
          redacted={confirm.redacted}
          onClick={confirm.onClick}
        >
          {confirm.label}
        </Button>
      </div>
    </div>
  );
}

export function Confirm({
  tone = 'down',
  actTone = 'danger',
  said,
  keep,
  onKeep,
  act,
  onAct,
  busy,
  disabled = false,
  children,
}: {
  tone?: 'down' | 'warn';
  /** Danger for an act that takes something away; primary for consent given. */
  actTone?: 'danger' | 'primary';
  said: ReactNode;
  keep: string;
  onKeep: () => void;
  act: string;
  onAct: () => void;
  busy: boolean;
  disabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-control p-2.5',
        tone === 'warn' ? 'bg-warn/10' : 'bg-down/10',
      )}
    >
      <p className={clsx('text-xs leading-relaxed', tone === 'warn' ? 'text-ink' : 'text-down')}>
        {said}
      </p>
      {children}
      <div className="flex justify-end gap-2">
        <Button onClick={onKeep}>{keep}</Button>
        <Button tone={actTone} busy={busy} disabled={disabled} onClick={onAct}>
          {act}
        </Button>
      </div>
    </div>
  );
}

/** A ticket's verdict: one large figure, what it is measured on under it, and a line beside it. */
export function TicketHead({
  figure,
  under,
  children,
}: {
  figure: ReactNode;
  under: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-x-4 gap-y-1.5 border-b border-line-soft pb-2.5 sm:flex-row sm:items-end">
      <div className="flex items-baseline gap-2 sm:flex-col sm:items-start sm:gap-0">
        <span className="tnum text-3xl leading-none font-semibold whitespace-nowrap">{figure}</span>
        <span className="tnum text-2xs text-ink-faint sm:mt-1">{under}</span>
      </div>
      <p className="tnum max-w-xl min-w-0 flex-1 text-xs leading-relaxed text-ink-muted">
        {children}
      </p>
    </div>
  );
}

/** A chip naming a price: what it is, the price, and faint beside it what it comes to; stacked on a phone. */
export function PriceChip({
  said,
  price,
  aside,
}: {
  said: string;
  price: string;
  aside?: string | null;
}) {
  return (
    <span className="tnum flex flex-col items-start leading-tight sm:inline sm:leading-normal">
      <span>{said}</span>{' '}
      <span>
        {price}
        {aside && <span className="ml-1.5 text-ink-faint">{aside}</span>}
      </span>
    </span>
  );
}

/** What confirming sets going, in order. */
export function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="tnum flex min-w-0 flex-1 flex-col gap-1 pl-5.5 text-xs text-ink-muted [&>li]:list-decimal [&>li]:marker:text-ink-faint">
      {children}
    </ol>
  );
}

/** A step: whether the desk squares off when the analyst closes, where it asks. */
export function OutWithIt({
  asks,
  checked,
  onChange,
}: {
  asks: boolean;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const said = 'Out when the analyst closes it.';
  if (!asks) return said;
  return (
    <Checkbox checked={checked} onChange={onChange} label={said}>
      {!checked && (
        <span className="text-xs text-ink-faint">
          I ask you to exit it instead; the fallback stop stays until you do.
        </span>
      )}
    </Checkbox>
  );
}

export function Preview({ children }: { children: ReactNode }) {
  return (
    <p className="tnum flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-ink-muted">
      {children}
    </p>
  );
}

const ALIGN = {
  center: { row: 'sm:items-center', label: '' },
  stepper: { row: 'sm:items-start', label: 'sm:leading-9' },
  start: { row: 'sm:items-start', label: 'sm:leading-5' },
};

export function Part({
  label,
  align = 'center',
  children,
}: {
  label: string;
  align?: keyof typeof ALIGN;
  children: ReactNode;
}) {
  return (
    <div className={clsx('flex flex-col gap-x-3 gap-y-1 sm:flex-row', ALIGN[align].row)}>
      <span className={clsx('shrink-0 text-xs text-ink-faint sm:w-14', ALIGN[align].label)}>
        {label}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5">{children}</div>
    </div>
  );
}

export function Lots({
  count,
  unit,
  onCount,
  max,
}: {
  count: number;
  unit: (count: number) => string;
  onCount: (count: number) => void;
  max?: number;
}) {
  const step =
    'h-9 w-9 bg-surface text-base text-ink hover:bg-canvas disabled:text-ink-faint disabled:hover:bg-surface';
  return (
    <div className="flex items-center gap-3">
      {count > 0 && (
        <span className="tnum text-sm text-ink">
          {count} <span className="text-xs text-ink-muted">{unit(count)}</span>
        </span>
      )}
      <div className="flex items-center overflow-hidden rounded-control border border-line">
        <button
          type="button"
          aria-label="One lot fewer"
          disabled={count <= 1}
          onClick={() => onCount(count - 1)}
          className={step}
        >
          −
        </button>
        <button
          type="button"
          aria-label="One lot more"
          disabled={max !== undefined && count >= max}
          onClick={() => onCount(count + 1)}
          className={step}
        >
          +
        </button>
      </div>
    </div>
  );
}

export function Outcome({
  said,
  className,
  children,
}: {
  said: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <span className="flex flex-col">
      <span className="text-2xs text-ink-faint">{said}</span>
      <span className={clsx('text-sm', className)}>{children}</span>
    </span>
  );
}

export function BudgetBar({ risk, budget, said }: { risk: number; budget: number; said?: string }) {
  const over = risk > budget;
  const scale = Math.max(risk, budget) * 1.15;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative h-1.5 max-w-md rounded-full bg-line-soft" aria-hidden>
        <div
          className={clsx(
            'absolute inset-y-0 left-0 rounded-full',
            over ? 'bg-warn' : 'bg-ink-faint',
          )}
          style={{ width: `${Math.min(100, (risk / scale) * 100)}%` }}
        />
        <div
          className="absolute -inset-y-1 w-0.5 bg-ink"
          style={{ left: `${(budget / scale) * 100}%` }}
        />
      </div>
      <span className={clsx('text-xs', over ? 'text-warn' : 'text-ink-faint')}>
        {said} {over ? 'Past' : 'Within'} your {fmtRupees(budget)} a trade.
      </span>
    </div>
  );
}
